import { Dispatch, SetStateAction, useState } from 'react'
import { object } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { fileSchema, getFileInBase64 } from '@/Shared/ui/DropZone'
import { downloadExcel } from '@/Shared/domain/helpers'
import { useLoader } from '@/Shared/ui/loader'
import { Steps, stepSlug } from '@/Store/ui/atoms'
import { TFunction } from "i18next"
import { requiredCheckboxSchema, useMetaResponseHandler } from '@/Shared/ui/form'
import { useNotification } from '@/Shared/ui/notifications'

import { MessagesModel, MetaStatusCodes, NotificationSeverity } from '@/Shared/domain'
import { TpvExcelUploadResponse, TpvExcelInsertResultsResponse } from '@/Store/domain/TpvExcelUpload'
import { TpvsRepository } from '@/Store/domain'
import { TpvsContract } from '@/Store/application/Tpvs'

type UploadExcelForm = {
  file: FileList | null
}

type ValidateExcelForm = {
  terms: boolean
}

export type UseExcelUploadController = {
  step: Steps | undefined
  setStep: Dispatch<SetStateAction<Steps | undefined>>
  uploadForm: UseFormReturn<UploadExcelForm>
  validateForm: UseFormReturn<ValidateExcelForm>
  validFileFormats: string[]
  uploading: boolean
  uploadResponse: TpvExcelUploadResponse | undefined | null
  insertResultsResponse: TpvExcelInsertResultsResponse | undefined | null
  download: (empty: boolean) => Promise<void>
  upload: (data: UploadExcelForm) => Promise<void>
  downloadErrorsFile: () => Promise<void>
  confirm: (data: ValidateExcelForm) => Promise<void>
  clearErrors: () => void
  error: MessagesModel | undefined
  setError: Dispatch<SetStateAction<MessagesModel | undefined>>
  displayCancelModal: boolean
  setDisplayCancelModal: Dispatch<SetStateAction<boolean>>
  hasWrongFormatError: boolean
  setHasWrongFormatError: Dispatch<SetStateAction<boolean>>
  displayDetailModal: boolean
  setDisplayDetailModal: Dispatch<SetStateAction<boolean>>
  detailEmployeeList?: string[]
  setDetailEmployeeList?: Dispatch<SetStateAction<string[]>>
  detailTitle?: string
  setDetailTitle?: Dispatch<SetStateAction<string>>
  modalType?: string
  setModalType?: Dispatch<SetStateAction<string>>
  downloadInsertErrorsFile: () => Promise<void>
}

const validFileFormats = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
]

const maxFileSize = 6 * 1024 * 1024 // 6MB

const uploadSchema = (t: TFunction<'undefined', null>) =>
  object().shape({
    file: fileSchema(t, validFileFormats, maxFileSize).defined(),
  })

const validateSchema = (t: TFunction<'undefined', null>) =>
  object().shape({
    terms: requiredCheckboxSchema(t).required(),
  })

const getStep = (step?: string): Steps | undefined => {
  switch (step) {
    case stepSlug(Steps.Upload):
      return Steps.Upload
    case stepSlug(Steps.Validation):
      return Steps.Validation
    case stepSlug(Steps.Confirmation):
      return Steps.Confirmation
    default:
      return undefined
  }
}

export const useExcelUploadController = (
  repository: TpvsRepository,
  merchantTargetId: number,
) => {
  const [step, setStep] = useState<Steps | undefined>(getStep())
  const { t } = useTranslation()
  const [uploading, setUploading] = useState(false)
  const [uploadResponse, setUploadResponse] = useState<TpvExcelUploadResponse>()
  const [insertResultsResponse, setInsertResultsResponse] = useState<TpvExcelInsertResultsResponse>()
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<MessagesModel>()
  const [hasWrongFormatError, setHasWrongFormatError] = useState<boolean>(false)
  const [displayCancelModal, setDisplayCancelModal] = useState<boolean>(false)
  const [displayDetailModal, setDisplayDetailModal] = useState<boolean>(false)
  const [detailEmployeeList, setDetailEmployeeList] = useState<string[]>([])
  const [detailTitle, setDetailTitle] = useState<string>('')
  const [modalType, setModalType] = useState<string>('')
  const { addNotification } = useNotification()

  const uploadForm = useForm<UploadExcelForm>({
    mode: 'onChange',
    resolver: yupResolver(uploadSchema(t)),
    defaultValues: {
      file: null,
    },
  })

  const validateForm = useForm<ValidateExcelForm>({
    mode: 'onChange',
    resolver: yupResolver(validateSchema(t)),
    defaultValues: {
      terms: false,
    },
  })

  const { handleMetaResponse } = useMetaResponseHandler()
  const { startLoading, stopLoading } = useLoader()

  const download = async (empty: boolean): Promise<void> => {
    startLoading()

    const response = await TpvsContract(repository).getTerminalsMassiveExcelTemplate(
      merchantTargetId,
      empty,
    )

    stopLoading()

    if (
      handleMetaResponse(response?.meta, undefined, {
        notifySuccess: false,
      }) &&
      response.data?.file
    ) {
      await downloadExcel(response.data?.file, 'terminales')
    }
  }

  const upload = async (data: UploadExcelForm): Promise<void> => {
    setUploading(true)

    const base64File = await getFileInBase64(data.file)

    if (!base64File) {
      setUploading(false)
      addNotification('notification error', NotificationSeverity.error)

      return
    }

    const response = await TpvsContract(repository).validateOrder({ excel: base64File })

    if (response.meta.status === MetaStatusCodes.SUCCESS) {
      validateForm.reset()
      setUploadResponse(response.data)
      const insertResults = await TpvsContract(repository).validateAndInsertTPVsExcel({ excel: base64File })
      setInsertResultsResponse(insertResults.data)
      if (
        (insertResults.data as unknown as TpvExcelInsertResultsResponse)?.insertResults?.some(
          item => !item.isSuccess
        )
      ) {
        addNotification(t('excelTPVConfirmation.errors.toastMessage'), NotificationSeverity.error)
      } else {
        addNotification(t('excelTPVConfirmation.successToastMessage'), NotificationSeverity.success)
      }
      setStep(Steps.Confirmation)
      setUploading(false)
    }

    if (response.meta.status === MetaStatusCodes.ERROR) {
      setStep(Steps.Validation)
      setUploadResponse(response.data)
      setUploading(false)
    }
  }

  const downloadErrorsFile = async (): Promise<void> => {
    if (uploadResponse?.fileResponse) {
      await downloadExcel(uploadResponse.fileResponse, 'errors')
    }
  }

  const downloadInsertErrorsFile = async (): Promise<void> => {
    if (insertResultsResponse?.fileResponse) {
      await downloadExcel((insertResultsResponse.fileResponse as unknown as { file: string }).file, 'errors')
    }
  }


  const clearErrors = () => {
    setUploadResponse(undefined)
    setSuccess(false)

    uploadForm.reset()
  }

  return {
    step,
    setStep,
    uploadForm,
    validateForm,
    validFileFormats,
    uploading,
    uploadResponse,
    download,
    upload,
    downloadErrorsFile,
    confirm,
    clearErrors,
    error,
    setError,
    displayCancelModal,
    setDisplayCancelModal,
    hasWrongFormatError,
    setHasWrongFormatError,
    displayDetailModal,
    setDisplayDetailModal,
    detailEmployeeList,
    setDetailEmployeeList,
    detailTitle,
    setDetailTitle,
    setModalType,
    modalType,
    insertResultsResponse,
    downloadInsertErrorsFile,
  }
}
